import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {LoadingService} from '../../../../../shared/services/loading.service';
import {HolidayBookingService} from '../../../holiday-booking-page/services/holiday-booking.service';
import {HolidayDetailService} from '../../services/holiday-detail.service';
import {IHolidayDetail} from '../../interfaces/iholiday-detail';
import {IHolidayDetailSolution} from '../../interfaces/iholiday-detail-solution';
import {Observable, Subject, Subscription} from 'rxjs';
import groupBy from '../../../../../../utils/groupBy';
import * as dayjs from 'dayjs';
import {takeUntil} from 'rxjs/operators';

@Component({
  selector: 'detail-price-grid',
  templateUrl: './detail-price-grid.component.html',
  styleUrls: ['./detail-price-grid.component.scss']
})
export class DetailPriceGridComponent implements OnInit, OnDestroy {

  @Input() set holidayDetails(holidayDetails: IHolidayDetail) {
    // metodo eseguito ad ogni update del "holidayDetails" passato al componente
    this.updateHolidayDetails(holidayDetails);
  }

  public config = {
    breakpoints: {
      320: {
        slidesPerView: 1,
        simulateTouch: true
      },
      480: {
        slidesPerView: 1,
        simulateTouch: true
      },
      768: {
        slidesPerView: 3,
        simulateTouch: true
      },
      992: {
        slidesPerView: 4,
        simulateTouch: false,
        allowTouchMove: false
      },
      1200: {
        slidesPerView: 5,
        simulateTouch: false,
        allowTouchMove: false
      },
      1600: {
        slidesPerView: 5,
        simulateTouch: false,
        allowTouchMove: false
      }
    },
    slidesPerView: 5,
    centeredSlides: false,
    navigation: {
      nextEl: '.swiper-button-next',
      prevEl: '.swiper-button-prev',
    }
  };
  public index;

  public classicLoadingStatus$: Observable<boolean>;
  public classicLoadingStatusSubscription: Subscription;

  public isLoading: boolean;

  public availablesParentTypes: string[];
  public availablesMonths: string[];
  public availablesNights: number[];

  public solutionsGroupedForPriceGrid: any;
  public departurePlacesGroupedForPriceGrid: any;

  public selectedParentType: string;
  public selectedMonth: string;
  public selectedNights: number;
  public selectedSolution: IHolidayDetailSolution;

  // serve esclusivamente per inserire una classe "active-row" nella riga (evidenziandola) della griglia con la
  // solution selezionata
  public selectedDeparturePlaceIdByTypeMonthAndNights: any;

  private allSolutions: IHolidayDetailSolution[];
  private solutionsFilteredByParentType: IHolidayDetailSolution[];

  private unsubscribe$ = new Subject();

  constructor(
    private _holidayDetailService: HolidayDetailService,
    private _loadingService: LoadingService,
    private _holidayBookingService: HolidayBookingService
  ) {
  }

  ngOnInit(): void {
    this.classicLoadingStatus$ = this._loadingService.getClassicLoadingStatus();

    this.classicLoadingStatusSubscription = this.classicLoadingStatus$
      .subscribe((loadingStatus) => {
        this.isLoading = loadingStatus;
      });

    this._holidayBookingService.selectedSolution$
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((selectedSolution: IHolidayDetailSolution) => {
        if(!selectedSolution) {
          return;
        }
        selectedSolution.isShownPriceLive = true;
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.classicLoadingStatusSubscription?.unsubscribe();
    // resetto la solution selezionata
    this._holidayBookingService.selectedSolution$.next(undefined);
  }

  public selectParentType(parentType: string, selectedMonth?: string, selectedNights?: number): void {
    this.selectedParentType = parentType;

    // filtro le solutions in base al "ParentType" selezionato e le salvo in un array dedicato in modo da averle
    // gia filtrate
    this.solutionsFilteredByParentType = this.allSolutions.filter((sol) => sol.parentType === this.selectedParentType);

    // aggiorno i filtri della griglia dedicate ai mesi e le notti/durate in base alle solutions filtrate
    this.availablesMonths = this.getAvailableMonths(this.solutionsFilteredByParentType);
    this.availablesNights = this.getAvailableNights(this.solutionsFilteredByParentType);

    if (selectedMonth) {
      // se nel metodo ho passato il mese da selezionare (se la notte non è stata passata in ogni caso verrà presa
      // quella di default dall'array delle notti/durate disponibili) ...
      this.selectMonth(selectedMonth, selectedNights);
    } else if (this.availablesMonths.length > 0) {
      // ... altrimenti, verifico se sono presenti mesi selezionabili ed estraggo dall'array il primo disponibile
      // in modo da selezionarlo di default (caso di selezione mese dalla griglia)
      const [firstMonthAvailable] = this.availablesMonths;

      // seleziono il primo mese estratto
      this.selectMonth(firstMonthAvailable);
    }
  }

  public selectMonth(month: string, selectedNights?: number): void {
    this.selectedMonth = month;

    // raggruppo le solutions per mesi dall'array di solutions filtrate per "parentType"
    const solutionsByMonth = groupBy(this.solutionsFilteredByParentType, (sol) => dayjs(sol.dateFrom).format('MMM YYYY'));

    // recupero le solutions del mese selezionato
    const selectedSolutionsOfMonth = solutionsByMonth[this.selectedMonth];

    // aggiorno le notti/durate disponibili in base alle solutions del mese selezionato
    this.availablesNights = this.getAvailableNights(selectedSolutionsOfMonth);

    if (selectedNights) {
      // se nel metodo ho passato le notti/durate da selezionare ...
      this.selectNight(selectedNights);
    } else if (this.availablesNights.length > 0) {
      // ... altrimenti, verifico se sono presenti notti/durate selezionabili ed estraggo dall'array la prima
      // disponibile in modo da selezionarla di default (caso di selezione notti/durate dalla griglia)
      const [firstNightAvailable] = this.availablesNights;

      // seleziono la prima notte estratta dall'array
      this.selectNight(firstNightAvailable);
    }
  }

  public selectNight(nights: number): void {
    this.selectedNights = nights;
  }

  public selectSolution(solution: IHolidayDetailSolution): void {
    // se ancora è in esecuzione il loader, non proseguo evitando possibili errori
    if (this.isLoading) {
      return;
    }
    this.selectedSolution = solution;

    // aggiorno la solution selezionata tramite BehaviorSubject in modo da aggiornarla anche su tutti
    // i componenti registrati
    this.updateSelectedSolution(solution);
  }

  private updateHolidayDetails(holidayDetails: IHolidayDetail): void {
    // se è presente "holidayDetails" (passato al componente tramite @Input) ...
    if (holidayDetails) {
      // "copio" l'array delle solutions disponibili del dettaglio della holiday passato al componente
      this.allSolutions = holidayDetails.solutions;

      // "copio" la solution selezionata del dettaglio holiday (ottenuta selezionandola dal listato di ricerca)
      // passato al componente
      this.selectedSolution = holidayDetails.searchSolution;

      // chiamo il metodo che si occupa di inizializzare le varie proprietà per il funzionamento della
      // griglia solutions/prezzi
      this.initPriceGrid(this.allSolutions, this.selectedSolution);
    }
  }

  private initPriceGrid(allSolutions: IHolidayDetailSolution[], selectedSolution: IHolidayDetailSolution): void {

    if (!selectedSolution) {
      [selectedSolution] = allSolutions;
    }

    // recupero il departurePlace della solution selezionata passata nel metodo
    const selectedDeparturePlace = selectedSolution?.airport || null;

    // recupero i "parentTypes", i mesi e le notti/durate disponibili dalla solution selezionata passata nel metodo
    const selectedParentType = selectedSolution?.parentType;
    const selectedSolutionDepartureDate = selectedSolution?.dateFrom ? dayjs(selectedSolution.dateFrom).format('MMM YYYY') : null;
    const selectedNights = selectedSolution?.nights;

    // salvo il departurePlaceId della solution selezionata (searchSolution ottenuta dalla selezione nel listato
    // di ricerca)
    this.selectDeparturePlaceIdByTypeMonthAndNights(
      selectedDeparturePlace,
      selectedParentType,
      selectedSolutionDepartureDate,
      selectedNights.toString()
    );

    // recupero le solutions disponibili raggruppate in un formato adatto alla griglia
    this.solutionsGroupedForPriceGrid = this._holidayDetailService.getSolutionsGroupedForPriceGrid(allSolutions, selectedSolution);

    // recupero i departurePlacesId raggruppati in un formato adatto per ciclare le righe della griglia
    this.departurePlacesGroupedForPriceGrid = this._holidayDetailService.getDeparturePlacesGroupedForPriceGrid(allSolutions);

    // inizio a popolare gli array dei "parentTypes", mesi e notti/durate disponibili recuperandole dall'array delle
    // solutions passate nel metodo
    this.availablesParentTypes = this.getAvailableParentTypes(allSolutions);
    this.availablesMonths = this.getAvailableMonths(allSolutions);
    this.availablesNights = this.getAvailableNights(allSolutions);

    // seleziono il "parentType" della solution selezionata
    // NB: il metodo esegue tutto il flusso per poter poi successivamente selezionare il mese e la notte dalla solution
    // già selezionata (primo caricamento del dettaglio holiday)
    this.selectParentType(selectedParentType, selectedSolutionDepartureDate, selectedNights);
  }

  private getAvailableParentTypes(solutions: IHolidayDetailSolution[]): string[] {
    const allParentTypes = solutions.map(sol => sol.parentType);

    // rimuovo eventuali duplicati e ritorno l'array di "parentType" disponibili
    return [...new Set(allParentTypes)];
  }

  private getAvailableMonths(solutions: IHolidayDetailSolution[]): string[] {
    // raggruppo le solutions ordinate per mese
    const allMonths = solutions
      .sort((a, b) => dayjs(a.dateFrom).unix() - dayjs(b.dateFrom).unix())
      .map(sol => dayjs(sol.dateFrom).format('MMM YYYY'));

    // rimuovo eventuali duplicati e ritorno l'array di mesi disponibili
    return [...new Set(allMonths)];
  }

  private getAvailableNights(solutions: IHolidayDetailSolution[]): number[] {
    // recupero le notti/durate ordinate in modo crescente
    const allNights = solutions
      .sort((a, b) => a.nights - b.nights)
      .map(sol => sol.nights);

    // rimuovo eventuali duplicati e ritorno l'array delle notti/durate disponibili
    return [...new Set(allNights)];
  }

  private updateSelectedSolution(solution: IHolidayDetailSolution): void {
    // prima di aggiornare la solution, riformatto le date di partenza e di arrivo per evitare possibili
    // problemi di formattazione date su altri componenti durante l'aggiornamento tramite BehaviorSubject
    solution.dateFrom = solution.dateFrom ? dayjs(solution.dateFrom).toDate() : solution.dateFrom;
    solution.dateTo = solution.dateTo ? dayjs(solution.dateTo).toDate() : solution.dateTo;

    // aggiorno la solution selezionata tramite BehaviorSubject in modo da aggiornarla anche su
    // tutti i componenti registrati
    this._holidayBookingService.selectedSolution$.next(solution);
  }

  private selectDeparturePlaceIdByTypeMonthAndNights(
    selectedDeparturePlace: number | null,
    selectedParentType: string,
    selectedSolutionDepartureDate: string,
    selectedNights: string
  ): void {
    // inizio a creare la struttura dell'oggetto che conterrà il departurePlaceId organizzato per "parentType", mese e
    // notti/durata selezionata
    if (!this.selectedDeparturePlaceIdByTypeMonthAndNights) {
      this.selectedDeparturePlaceIdByTypeMonthAndNights = {};
    }
    if (!this.selectedDeparturePlaceIdByTypeMonthAndNights[selectedParentType]) {
      this.selectedDeparturePlaceIdByTypeMonthAndNights[selectedParentType] = {};
    }
    if (!this.selectedDeparturePlaceIdByTypeMonthAndNights[selectedParentType][selectedSolutionDepartureDate]) {
      this.selectedDeparturePlaceIdByTypeMonthAndNights[selectedParentType][selectedSolutionDepartureDate] = {};
    }
    if (!this.selectedDeparturePlaceIdByTypeMonthAndNights[selectedParentType][selectedSolutionDepartureDate][selectedNights]) {
      // salvo il departurePlace
      this.selectedDeparturePlaceIdByTypeMonthAndNights[selectedParentType][selectedSolutionDepartureDate][selectedNights] =
        selectedDeparturePlace;
    }
  }
}
